import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import type { ReactNode } from 'react'
import { type Blocker } from 'react-router'
import { isEqual } from 'lodash'
import { AxiosError } from 'axios'
import { nanoid } from 'nanoid'
import { toastStore } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { KeywordsApi } from 'entities/Keywords/api/keywords'
import type { IParamsKeyword, IResponseKeyword } from 'entities/Keywords/api/type'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { TagsDropdownControlStore } from 'widgets/TagsDropdown/store/tagsDropdownControlStore'
import { SendFromMultiStore } from 'widgets/SendFromMulti'
import { EnumModeKeyword, type IKeywordCreateField, IKeywordCreateFieldEnum } from './type'
import { KeywordSMSStore } from './keywordSMSStore'

export class KeywordViewStore {
  private _isLoading = true
  private _isSaving = false
  private _name = ''
  private _title = ''
  private _errorsMap = new Map<IKeywordCreateField, ReactNode>()
  private _isDragging = false
  private _mergeFieldsProps = {
    isAllConnectedOrDisconnectedIntegration: false,
    isSelectDefault: true,
    useAllMergeFields: true,
  }
  private _cacheParams: IParamsKeyword | null = null
  private _modalKeepEditing = nanoid()
  private _disposeIsAircallReaction: IReactionDisposer | null = null
  private _mode: EnumModeKeyword = EnumModeKeyword.create
  private _editKeyword: IResponseKeyword | null = null
  private readonly _keywordSMSStore: KeywordSMSStore
  private readonly _sendFromMultiStore: SendFromMultiStore
  private _tagsControlStore = new TagsDropdownControlStore()

  constructor(
    private _entities: IEntitiesStore,
    keywordResponse?: IResponseKeyword,
    isDuplicate?: boolean
  ) {
    makeAutoObservable(this)

    this._sendFromMultiStore = new SendFromMultiStore(this._entities, {
      getDropdownItemsOptions: {
        disabledClosedTooltip: true,
        excludeSendingOptions: true,
        showOnlyVerifiedToolFree: true,
      },
    })
    this._keywordSMSStore = new KeywordSMSStore(
      {
        onFocus: () => {
          this.removeError('message')
        },
        placeholder: 'Write your auto-reply message...',
        mergeFieldsProps: this._mergeFieldsProps,
        isMarketing: true,
      },
      this._entities
    )
    void this.init(keywordResponse, isDuplicate)
  }

  init = async (keywordResponse?: IResponseKeyword, isDuplicate?: boolean) => {
    runInAction(() => {
      this._isLoading = true
    })

    await Promise.all([
      this._entities.integrationsStore.fetchIntegrations(),
      this._entities.mergeFieldStore.loadAllFields(),
    ])
    await this._sendFromMultiStore.initPromise

    if (keywordResponse) {
      if (isDuplicate) {
        runInAction(() => {
          this._title = 'Copy of: ' + keywordResponse.title
        })
      } else {
        runInAction(() => {
          this._editKeyword = keywordResponse
          this._mode = EnumModeKeyword.edit
          this._title = keywordResponse.title
        })
      }

      runInAction(() => {
        this._name = keywordResponse.name
      })

      if (keywordResponse.tags?.length) {
        this._tagsControlStore.init(keywordResponse.tags)
      }
      this._keywordSMSStore.init(keywordResponse)
      void this._sendFromMultiStore.setNumbersIds(
        keywordResponse.numbers.map((number) => number.id)
      )
    }

    if (!isDuplicate) {
      runInAction(() => {
        this._cacheParams = this.keywordParams
      })
    }

    this.reactionIsAircall()

    runInAction(() => {
      this._isLoading = false
    })
  }

  get sendFromMultiStore() {
    return this._sendFromMultiStore
  }

  get isLoading() {
    return this._isLoading
  }

  get name() {
    return this._name
  }

  get title() {
    return this._title
  }

  get keywordSMSStore() {
    return this._keywordSMSStore
  }

  get isDraggingCondition() {
    return this._isDragging
  }

  get tagsControlStore() {
    return this._tagsControlStore
  }

  get errorsMap() {
    return this._errorsMap
  }

  get isSaving() {
    return this._isSaving
  }

  get keywordParams(): IParamsKeyword {
    return {
      id: this.isEditMode && this._editKeyword ? this._editKeyword.id : null,
      name: this.name,
      title: this.title,
      is_active: this.isEditMode && this._editKeyword ? this._editKeyword?.is_active : false,
      numbers: this._sendFromMultiStore.numbers_ids,
      tags: this.tagsControlStore.tags.map((tag) => tag.id),
      ...this.keywordSMSStore.messageDataParams,
    }
  }

  get isExistChanges() {
    return !isEqual(this._cacheParams, this.keywordParams)
  }

  get isEditMode() {
    return this._mode === EnumModeKeyword.edit
  }

  setName = (name: string) => {
    this._name = name
  }

  setTitle = (title: string) => {
    this._title = title
  }

  setError = (key: IKeywordCreateField, error: ReactNode) => {
    if (key === 'numbers') {
      this._sendFromMultiStore.setError(error as string)
    }
    this._errorsMap.set(key, error)
  }

  removeError = (key: IKeywordCreateField) => {
    if (key === 'numbers') {
      this._sendFromMultiStore.setError('')
    }
    this._errorsMap.delete(key)
  }

  setDraggingCondition = (condition: boolean) => {
    this._isDragging = condition
  }

  clearErrors = () => {
    this._sendFromMultiStore.clearError?.()
    this.errorsMap.clear()
  }

  private saveKeyword = async (keywordParams: IParamsKeyword): Promise<boolean> => {
    const cacheParams = this._cacheParams
    try {
      runInAction(() => {
        this._isSaving = true
        this._cacheParams = keywordParams
      })

      if (this.isEditMode) {
        await KeywordsApi.updateKeyword(keywordParams)
      } else {
        await KeywordsApi.createKeyword(keywordParams)
      }

      toastStore.add({
        type: 'success',
        title: this.isEditMode ? 'Keyword saved' : 'Keyword created',
      })
      return true
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.errors.getError('error')) {
          toastStore.add({ type: 'error', title: e.errors.getError('error') })
        } else {
          const isKeywordField = (key: string): key is IKeywordCreateField =>
            key in IKeywordCreateFieldEnum
          e.errors.entries().forEach(([key, value]) => {
            if (isKeywordField(key)) {
              this.setError(key, value.value)
            } else {
              toastStore.add({ type: 'error', title: value.value })
            }
          })
        }
      }
      this._cacheParams = cacheParams
      return false
    } finally {
      runInAction(() => {
        this._isSaving = false
      })
    }
  }

  handleSaveKeyword = async () => {
    this.clearErrors()
    this.checkValidate()
    if (this.errorsMap.size || this.keywordSMSStore.isError) {
      return
    }

    const isSaveSuccessful = await this.saveKeyword(this.keywordParams)
    if (isSaveSuccessful) {
      uiStore.changeRoute({ path: '/keywords' })
    }
  }

  checkValidate = () => {
    const { name, title, numbers } = this.keywordParams
    const lettersNumsSomeSymbolsRegex = /^[A-Za-z0-9_#-]+$/

    if (!name.trim().length) {
      this.setError('name', 'Please enter keyword name')
    }

    if (!lettersNumsSomeSymbolsRegex.test(name)) {
      this.setError(
        'name',
        'Invalid keyword name. Only letters, numbers, some special symbols (_, #, - ) are allowed'
      )
    }

    if (!title.trim().length) {
      this.setError('title', 'Please enter internal description')
    }

    if (!numbers.length) {
      this.setError('numbers', 'Please select the phone number(s)')
    }
  }

  handleShowKeepEditingModal = (blocker: Blocker) => {
    if (blocker.state !== 'blocked') return

    modalStore.addModal({
      id: this._modalKeepEditing,
      type: ModalTypeList.WARNING,
      title: 'Leaving will discard your content',
      primaryAction: {
        text: 'Keep content',
        onAction: () => {
          modalStore.closeModal(this._modalKeepEditing)
        },
      },
      secondaryAction: {
        text: 'Discard',
        onAction: () => {
          const path = blocker.location.pathname
          blocker.proceed()

          uiStore.setRoutesBlocker(null)
          uiStore.changeRoute({
            path,
            type: 'vue',
            method: 'replace',
          })
          modalStore.closeModal(this._modalKeepEditing)
        },
      },
    })
  }

  reset = () => {
    this._disposeIsAircallReaction?.()
  }

  reactionIsAircall = () => {
    this._disposeIsAircallReaction?.()
    this._disposeIsAircallReaction = reaction(
      () => this._sendFromMultiStore.isAircall,
      (isAircall) => {
        this._keywordSMSStore.messageFieldStore.setIsCurrentAirCall(isAircall)
      }
    )
  }
}
